import type {
  Id,
  Graph,
  NodeVariable,
  Criteria,
  JobMatchNode as JobMatchNodeType,
  JobMatchConfig,
} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector} from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import take from 'lodash/take';
import isEmpty from 'lodash/isEmpty';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';

import {useChatbotStateContext} from '../chatbot-state-context';
import {useMutableFieldStatus} from '../api-hooks';

import * as api from 'src/utils/api-no-store';
import produce from 'immer';

import {
  createJobMatchNode,
  createEditableGraph,
  getMaxMatchesOptions,
  getNoMatchHandlingOptions,
  findAncestorNodeVariables,
  findAncestorFileNodes,
  canJobMatchBeQuestionNode,
} from '../utils';

import {validateNode} from '../node-editor/validation';

import {useFirstSmsMessage} from '../node-editor/common';
import {MixVariableInput} from '../common';
import classify from 'src/utils/classify';

import {Button} from 'src/designSystem2021Components/buttons';
import {
  FormInputMedium,
  BodySmallBold,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';

import Loading from 'src/components/lib/loading';

import {
  useChatbotJobMatchWorksiteOptions,
  useReleaseFlag,
} from 'src/hooks/product-flags';
import {selectAgencyConfig} from 'src/selectors/agency';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import RichEditor, {
  RichEditorPreview,
} from 'src/components/lib/markdown-editor/genesis';

import {Input} from 'src/designSystem2021Components/input';
import {Select} from 'src/designSystem2021Components/select';
import {Smallest} from 'src/designSystem2021Components/text-v2';
import {TokenListInput} from 'src/components/lib/token-list-input';

import {type MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {
  Dropdown,
  SimpleDropdown,
} from '@spaced-out/ui-design-system/lib/components/Dropdown';

import {Input as GenesisInput} from '@spaced-out/ui-design-system/lib/components/Input';
import {CloseIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button as GenesisButton} from '@spaced-out/ui-design-system/lib/components/Button';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Toggle} from '@spaced-out/ui-design-system/lib/components/Toggle';
import {Avatar} from '@spaced-out/ui-design-system/lib/components/Avatar';
import {BOT_USE_CASES} from 'src/components/conversation-builder/constants';

import {
  getSelectedKeysFromSelectedOption,
  getTextLabelFromSelectedKeys,
} from '@spaced-out/ui-design-system/lib/utils/menu';

import {useChatbotVariables, useEditorState} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import {type RawDraftContentState, type DraftDecoratorType} from 'draft-js';


export default function JobMatchNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t, makeKey} = useLabelTranslation();
  const extendedCriteriaEnabled = useChatbotJobMatchWorksiteOptions();

  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );

  const searchResultFormattingEnabled = useReleaseFlag(
    'chatbotCustomJobResults',
  );
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );
  const jmQnEnabled = useReleaseFlag('chatbotInstantApply2');
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const dynamicJobMatchAttributesEnabled = useReleaseFlag(
    'chatbotFetchJobMatchCriteriaFromDiscover',
  );

  const agencyConfigJobSearchParams = jobSearchParams;

  const agencyConfigs = useSelector(selectAgencyConfig);

  const {state, dispatch} = useChatbotStateContext();
  const editableGraph = createEditableGraph(graph);

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'job_match'
      ? editingNodeData
      : createJobMatchNode(extendedCriteriaEnabled, true, true, graph.use_case);

  const [editorState, editorDispatch] = useEditorState<JobMatchNodeType>({
    defaultNode,
  });

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const [showJobMatchCriteriaSelector, setShowJobMatchCriteriaSelector] =
    React.useState(false);

  const {node} = editorState;
  const setNode = (payload: JobMatchNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const jobMatchConfig: JobMatchConfig = node.job_search_criteria_data;

  // @ts-expect-error - TS7006 - Parameter 'job_search_criteria_data' implicitly has an 'any' type.
  const setJobMatchConfig = (job_search_criteria_data) =>
    setNode({...node, job_search_criteria_data});

  const entityType = graph.anchor_entity_type;
  const edgeId = Number(state.insertingNode?.edgeId) || state.editingNode;

  // @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'number'.
  const anscestorQuestions = !isNaN(edgeId)
    ? findAncestorNodeVariables(
        createEditableGraph(graph),
        node,
        Number(edgeId),
        false,
        false,
        allowMeetingNodeVariables,
      )
    : [];

  const nodeIdOptions = anscestorQuestions.map(
    ({derived_field_name, name, node_id}) => ({
      value: {node_id, name: String(derived_field_name ?? name)},
      label: name,
    }),
  );
  const fileUploadNodeOptions = findAncestorFileNodes(
    editableGraph,
    node,
    Number(edgeId),
  ).map(({node_id, name}) => ({
    label: name,
    key: String(node_id),
  }));

  const writebackAttributes = useChatbotVariables(entityType, {
    showDiscoverAttributes: false,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });
  const isSourcing = graph.use_case === 'sourcing';

  const ancestorDataByName = anscestorQuestions.reduce<Record<string, any>>(
    (result, nodeValue) => {
      const key = nodeValue.derived_field_name ?? nodeValue.name;
      result[key] = nodeValue;
      return result;
    },
    {},
  );

  // returns an object keyed by node_id and then by derived_field_name ?? name
  // this deeply nested object returns a nodeValue. We use this because we save
  // reference_node_ids as field_name and node_id and only from this pair can we
  // accurately get a display name
  const anscestorDataByNodeIdAndName: Partial<
    Record<
      Id,
      {
        [name: string]: NodeVariable | null | undefined;
      }
    >
  > = anscestorQuestions.reduce<Record<string, any>>((result, nodeValue) => {
    const key = nodeValue.node_id;
    if (result[key] == null) {
      result[key] = {};
    }
    result[key][nodeValue.derived_field_name ?? nodeValue.name] = nodeValue;
    return result;
  }, {});

  const mapReferenceNodeIdValues = (criterion: Criteria) => {
    const nodeValues =
      jobMatchConfig.criteria[criterion]?.reference_node_ids ?? [];
    if (typeof nodeValues[0] === 'object') {
      return nodeValues.map(({field_name, node_id}) => {
        let label = ancestorDataByName[field_name]?.name;
        if (node_id) {
          const nodeById = anscestorDataByNodeIdAndName[node_id];

          label =
            nodeById &&
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            (anscestorDataByNodeIdAndName[node_id][field_name]?.name ??
              // @ts-expect-error - TS2769 - No overload matches this call.
              Object.keys(anscestorDataByNodeIdAndName[node_id]).find(
                (nodeName) =>
                  nodeName.includes(field_name) ||
                  field_name.includes(nodeName),
              ));
        }

        return {
          value: {name: field_name, node_id},
          label,
          type: 'reference_node_ids',
        };
      });
    } else {
      return nodeValues.map((id) => {
        const nodeName =
          // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'number' and '{ node_id?: number | undefined; field_name: string; }' have no overlap.
          anscestorQuestions.find((node) => node.node_id === id)?.name ?? '';
        return {
          value: nodeName,
          label: nodeName,
          type: 'reference_node_ids',
        };
      });
    }
  };

  const mapAtsVariables = (criterion: Criteria) =>
    (jobMatchConfig.criteria[criterion]?.ats_variables ?? []).map(
      (atsVariable) => {
        // todo (AMAN): modify jobMatchConfig.criteria to include lable there only
        const option = writebackOptions.find(
          (option) => option.value === atsVariable,
        );
        return {
          value: atsVariable,
          label: `${option ? option.label : atsVariable}`,
          type: 'ats_variables',
        };
      },
    );
  const mapDefaultVars = (criterion: Criteria) =>
    (jobMatchConfig.criteria[criterion]?.default_values ?? []).map((val) => ({
      value: val,
      label: val,
      type: 'default_values',
    }));

  const getSelectedTokens = (criterion: Criteria) => {
    const nodeIds = mapReferenceNodeIdValues(criterion);
    const atsVariables = mapAtsVariables(criterion);
    const defaultVars = mapDefaultVars(criterion);
    return isSourcing
      ? [...defaultVars, ...nodeIds]
      : [...defaultVars, ...nodeIds, ...atsVariables];
  };

  const writebackOptions =
    writebackAttributes.map((data) => ({
      label: `<${data.fieldLabel ?? data.attributeName ?? data.name}>`,
      value: data.attributeName ?? data.value,
      entityRelationshipPath: data.entityRelationshipPath,
    })) ?? [];
  const tokenOptions = isSourcing
    ? nodeIdOptions
    : [...nodeIdOptions, ...writebackOptions];

  const isFirstSmsMessage = useFirstSmsMessage(
    createEditableGraph(graph),
    defaultNode,
  );

  const [dynamicJobMatchCriteria, setDynamicJobMatchCriteria] =
    React.useState<any>(null);
  const [sortOrderOptions, setSortOrderOptions] = React.useState<
    Array<{key: string; label: string; value: string; depends_on_either: any[]}>
  >([]);
  const [jobResultVariables, setJobResultVariables] = React.useState([]);
  const filteredSortOrderOptions = sortOrderOptions.filter(
    ({depends_on_either}) =>
      depends_on_either.length
        ? depends_on_either.some((dependencies) =>
            dependencies.every((dependency: string) =>
              Object.keys(jobMatchConfig.criteria).includes(dependency),
            ),
          )
        : true,
  );

  React.useEffect(() => {
    api.get('chatbot/job-match-params').then((res) => {
      if (res?.filter_params) {
        setDynamicJobMatchCriteria(res.filter_params);
        setSortOrderOptions(
          // @ts-expect-error - TS7006 - Parameter 'param' implicitly has an 'any' type.
          res.sort_params.map((param) => ({...param, key: param.value})),
        );
        setJobResultVariables(res.available_job_display_attributes);
      }
    });

    if (isEmpty(jobMatchConfig.job_result_rte)) {
      setJobMatchConfig({
        ...jobMatchConfig,
        matching_messages_rte: isEmpty(jobMatchConfig.matching_messages_rte)
          ? defaultMatchingMessage
          : jobMatchConfig.matching_messages_rte,
        job_result_rte: defaultJobResultFormat,
        show_all_jobs_button: true,
        result_display_style: 'No Styling',
      });
    }
  }, []);

  const categoryLabels = {
    location: t(
      'Location_Zip_City_State_Distance',
      'Location (Zip, City, State, Distance)',
    ),
    pay_rate: t(
      'Pay_rate_Currency_Amount_Frequency',
      'Pay rate (Currency, Amount, Frequency)',
    ),
  } as const;

  // getting unique categories from dynamicJobMatchCriteria
  const categories =
    (dynamicJobMatchCriteria &&
      uniqBy(dynamicJobMatchCriteria, ({category}) => category)
        // @ts-expect-error - TS2345 - Argument of type '({ category }: { category: any; }) => any' is not assignable to parameter of type '(value: unknown, index: number, array: unknown[]) => any'.
        .map(({category}) => category)
        .filter(Boolean)) ??
    [];

  const categorieLabelsFromAPI =
    (dynamicJobMatchCriteria || []) &&
    uniqBy(dynamicJobMatchCriteria, ({category}) => category)
      // @ts-expect-error - TS2769 - No overload matches this call.
      .filter(({category}) => category !== null)
      .reduce<Record<string, any>>(
        // @ts-expect-error - TS2345 - Argument of type '(acc: Record<string, any>, { category, category_label }: { category: any; category_label: any; }) => {}' is not assignable to parameter of type '(previousValue: Record<string, any>, currentValue: unknown, currentIndex: number, array: unknown[]) => Record<string, any>'.
        (acc, {category, category_label}) => ({
          ...acc,
          [category]: category_label,
        }),
        {},
      );

  const unselectedDynamicJobMatchCriteria = dynamicJobMatchAttributesEnabled
    ? (dynamicJobMatchCriteria || []).filter(
        // @ts-expect-error - TS7006 - Parameter 'criteria' implicitly has an 'any' type.
        (criteria) =>
          !Object.keys(jobMatchConfig.criteria).includes(criteria.id),
      )
    : dynamicJobMatchCriteria?.filter(
        (criteria: any) =>
          !Object.keys(jobMatchConfig.criteria).includes(criteria.id) &&
          (defaultJobSearchParams.includes(criteria.id) ||
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly worksite_options: "discoverAllowWorksiteOption"; readonly disciplines: "discoverAllowDiscipline"; readonly specialties: "discoverAllowSpecialty"; readonly certifications: "discoverAllowCertification"; readonly employment_types: "discoverAllowEmploymentType"; }'.
            (agencyConfigJobSearchParams[criteria.id] &&
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'AgencyConfig'. | TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly worksite_options: "discoverAllowWorksiteOption"; readonly disciplines: "discoverAllowDiscipline"; readonly specialties: "discoverAllowSpecialty"; readonly certifications: "discoverAllowCertification"; readonly employment_types: "discoverAllowEmploymentType"; }'.
              agencyConfigs[agencyConfigJobSearchParams[criteria.id]] !==
                'off')),
      ) ?? [];

  const uncategorizedDynamicJobMatchCriteria = unselectedDynamicJobMatchCriteria
    ?.filter((criteria: any) => !categories.includes(criteria.category))
    // @ts-expect-error - TS7031 - Binding element 'name' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type.
    .map(({name, id}) => ({label: name, key: id}));

  const categorizedDynamicJobMatchCriteria = categories
    // @ts-expect-error - TS7006 - Parameter 'categoryName' implicitly has an 'any' type.
    .map((categoryName) => {
      const criteriaInCategory = unselectedDynamicJobMatchCriteria.filter(
        // @ts-expect-error - TS7031 - Binding element 'category' implicitly has an 'any' type.
        ({category}) => category === categoryName,
      );
      return {
        // todo: consume label coming from API in here...
        label: dynamicJobMatchAttributesEnabled
          ? categorieLabelsFromAPI[categoryName]
          : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly location: string; readonly pay_rate: string; }'.
            categoryLabels[categoryName],
        key: categoryName,
        criteriaInCategory,
      };
    })
    // @ts-expect-error - TS7031 - Binding element 'criteriaInCategory' implicitly has an 'any' type.
    .filter(({criteriaInCategory}) => criteriaInCategory.length > 0);

  const dynamicJobMatchCriteriaOptions = [
    ...categorizedDynamicJobMatchCriteria,
    ...uncategorizedDynamicJobMatchCriteria,
    ...(!dynamicJobMatchAttributesEnabled
      ? [
          {
            label: t('Resume', 'Resume'),
            key: 'resume_url',
          },
        ]
      : []),
  ];

  const [showResumePicker, setShowResumePicker] = React.useState(
    Boolean(jobMatchConfig.resume_node_id),
  );

  const jobMatchCriteriaList = React.useMemo(() => {
    const selectedCriteriaNames = Object.keys(jobMatchConfig.criteria);
    // @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
    const selectedCriteria = dynamicJobMatchCriteria?.filter(({id}) =>
      selectedCriteriaNames.includes(id),
    );
    // @ts-expect-error - TS7034 - Variable 'result' implicitly has type 'any[]' in some locations where its type cannot be determined.
    const result = [];
    selectedCriteria?.forEach((criteriaData: any) => {
      const itemToAdd = {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Partial<Record<Criteria, { reference_node_ids: { node_id?: number | undefined; field_name: string; }[]; ats_variables: string[]; default_values?: string[] | undefined; }>>'.
        selectedValues: jobMatchConfig.criteria[criteriaData.id],
        criteriaData,
      } as const;
      if (criteriaData.category) {
        // @ts-expect-error - TS7005 - Variable 'result' implicitly has an 'any[]' type.
        const existingCategoryIndex = result.findIndex(
          (data) => data.category && data.category === criteriaData.category,
        );
        if (existingCategoryIndex > -1) {
          result[existingCategoryIndex] = {
            category: criteriaData.category,
            items: [
              // @ts-expect-error - TS7005 - Variable 'result' implicitly has an 'any[]' type.
              ...(result[existingCategoryIndex].items
                ? // @ts-expect-error - TS7005 - Variable 'result' implicitly has an 'any[]' type.
                  result[existingCategoryIndex].items
                : []),
              itemToAdd,
            ],
          };
        } else {
          result.push({
            category: criteriaData.category,
            items: [itemToAdd],
          });
        }
      } else {
        result.push(itemToAdd);
      }
    });
    // @ts-expect-error - TS7005 - Variable 'result' implicitly has an 'any[]' type.
    return result;
  }, [jobMatchConfig]);

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    Number(edgeId),
    false,
    false,
    allowMeetingNodeVariables,
  );

  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const dynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: false,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  // Validation
  const validation = validateNode(
    node,
    editableGraph,
    dynamicJobMatchCriteria,
    sortOrderOptions,
  );
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  const handleToggleQuestionNode = () => {
    const _jobMatchConfig = {
      ...jobMatchConfig,
    } as const;
    if (_jobMatchConfig.allow_application) {
      // @ts-expect-error - TS2540 - Cannot assign to 'allow_application' because it is a read-only property.
      _jobMatchConfig.allow_application = false;
      // @ts-expect-error - TS2540 - Cannot assign to 'allow_redirect' because it is a read-only property.
      _jobMatchConfig.allow_redirect = true;
    } else {
      // @ts-expect-error - TS2540 - Cannot assign to 'allow_application' because it is a read-only property.
      _jobMatchConfig.allow_application = true;
      // @ts-expect-error - TS2540 - Cannot assign to 'allow_redirect' because it is a read-only property.
      _jobMatchConfig.allow_redirect = false;
    }

    setJobMatchConfig(_jobMatchConfig);
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const isAnchorEntitySubmittal =
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    atsMappings[graph.anchor_entity_type].sense_name === 'job_submission';
  const isQuestionAllowed = canJobMatchBeQuestionNode(
    graph.use_case,
    graph.anchor_entity_type,
  );

  const resultDisplayOptions = [
    {value: 'No Styling', label: t('No_Styling', 'No Styling')},
    {value: 'Bulleted List', label: t('Bulleted_List', 'Bulleted List')},
    {value: 'Numbered List', label: t('Numbered_List', 'Numbered List')},
  ];

  const noMatchHandlingOptions = getNoMatchHandlingOptions(t);
  const maxMatchesOptions = getMaxMatchesOptions(t);

  if (!dynamicJobMatchCriteria) {
    return <Loading />;
  }

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.collapsibleHeader}>
        {t('Job_Match_Criteria', 'Job Match Criteria')}
      </div>
      <div className={css.section}>
        <p className={css.description}>{`${t(
          'Pick_a_node_value_or_variable_to_use_for_the_following_job_matching_criteria_If_a_node_value_is_left_blank_for_a_criteria_then_it_will_not_be_used_for_job_matching',
          'Pick a node value or variable to use for the following job matching criteria. If a node value is left blank for a criteria, then it will not be used for job matching',
        )}.`}</p>
        {/* show question node UI based on bot type */}
        {jmQnEnabled && isQuestionAllowed && isAnchorEntitySubmittal && (
          <div className={css.jobMatchBehaviourToggleContainer}>
            <Checkbox
              onChange={handleToggleQuestionNode}
              checked={jobMatchConfig.allow_application}
              disabled={
                graph.use_case === BOT_USE_CASES.APPLICATION_INBOUND_WEB
              }
            >
              {t(
                'Inbot_Application_Experience',
                'In-bot Application Experience',
              )}
            </Checkbox>
            {graph.use_case === BOT_USE_CASES.APPLICATION_INBOUND_WEB ? (
              <p>{`${t(
                'Note_Job_match_node_will_appear_as_question_allowing_users_to_select_and_apply_to_a_job_without_leaving_the_bot',
                'Note: Job match node will appear as question, allowing users to select and apply to a job without leaving the bot',
              )}.`}</p>
            ) : (
              <p>{`${t(
                'If_selected_job_matches_appear_as_question_allowing_users_to_apply_to_a_job_without_leaving_the_bot_Ensure_that_an_application_bot_is_linked_to_collect_an_application_If_unselected_candidate_is_redirected_to_the_JD_page_on_job_click',
                'If selected, job matches appear as question, allowing users to apply to a job without leaving the bot. Ensure that an application bot is linked to collect an application. If unselected, candidate is redirected to the JD page on job click',
              )}.`}</p>
            )}
          </div>
        )}
        <>
          {jobMatchCriteriaList.map((criteria, index) => {
            if (criteria.category) {
              return (
                <Card
                  classNames={{
                    wrapper: css.jobCategoryList,
                  }}
                  key={index}
                >
                  <div className={css.jobCategoryCardHeader}>
                    <SubTitleSmall>
                      {t(
                        makeKey(startCase(criteria.category)),
                        startCase(criteria.category),
                      )}
                    </SubTitleSmall>
                    {!readOnly && (
                      <CloseIcon
                        ariaLabel={t('Close_Icon', 'Close Icon')}
                        color="clickable"
                        onClick={() => {
                          setJobMatchConfig({
                            ...jobMatchConfig,
                            criteria: omit(
                              {...jobMatchConfig.criteria},
                              criteria.items.map(
                                // @ts-expect-error - TS7031 - Binding element 'criteriaData' implicitly has an 'any' type.
                                ({criteriaData}) => criteriaData.id,
                              ),
                            ),
                          });
                        }}
                        size="medium"
                        type="regular"
                      />
                    )}
                  </div>
                  {/* @ts-expect-error - TS7031 - Binding element 'criteriaData' implicitly has an 'any' type. | TS7031 - Binding element 'selectedValues' implicitly has an 'any' type. */}
                  {criteria.items.map(({criteriaData, selectedValues}) => (
                    <JobMatchCriteriaPickerV2
                      key={criteriaData.id}
                      // @ts-expect-error - TS2322 - Type '{ key: any; criteriaNodeData: any; criteriaData: any; values: ({ value: { name: string; node_id: number | undefined; }; label: any; type: string; } | { value: string; label: string; type: string; })[]; options: ({ ...; } | { ...; })[]; ... 8 more ...; readOnly: boolean; }' is not assignable to type 'IntrinsicAttributes & { values: any; jobMatchConfig: any; setJobMatchConfig: any; criteriaData: any; nodeIdOptions: any; writebackOptions: any; allowRemove?: boolean | undefined; includeATSVariables: any; readOnly: any; }'.
                      criteriaNodeData={selectedValues}
                      criteriaData={criteriaData}
                      values={getSelectedTokens(criteriaData.id) || []}
                      options={tokenOptions}
                      index={index}
                      jobMatchConfig={jobMatchConfig}
                      setJobMatchConfig={setJobMatchConfig}
                      nodeIdOptions={nodeIdOptions}
                      writebackOptions={writebackOptions}
                      mapReferenceNodeIdValues={mapReferenceNodeIdValues}
                      allowRemove={false}
                      includeATSVariables={!isSourcing}
                      readOnly={readOnly}
                    />
                  ))}
                </Card>
              );
            } else {
              const {criteriaData, selectedValues} = criteria;
              return (
                <JobMatchCriteriaPickerV2
                  key={criteriaData.id}
                  // @ts-expect-error - TS2322 - Type '{ key: any; criteriaNodeData: any; criteriaData: any; values: ({ value: { name: string; node_id: number | undefined; }; label: any; type: string; } | { value: string; label: string; type: string; })[]; options: ({ ...; } | { ...; })[]; ... 7 more ...; readOnly: boolean; }' is not assignable to type 'IntrinsicAttributes & { values: any; jobMatchConfig: any; setJobMatchConfig: any; criteriaData: any; nodeIdOptions: any; writebackOptions: any; allowRemove?: boolean | undefined; includeATSVariables: any; readOnly: any; }'.
                  criteriaNodeData={selectedValues}
                  criteriaData={criteriaData}
                  values={getSelectedTokens(criteriaData.id) || []}
                  options={tokenOptions}
                  index={index}
                  jobMatchConfig={jobMatchConfig}
                  setJobMatchConfig={setJobMatchConfig}
                  nodeIdOptions={nodeIdOptions}
                  writebackOptions={writebackOptions}
                  mapReferenceNodeIdValues={mapReferenceNodeIdValues}
                  includeATSVariables={!isSourcing}
                  readOnly={readOnly}
                />
              );
            }
          })}
          {showResumePicker && (
            <div className={css.jobMatchCriteriaInputRow}>
              <GenesisInput readOnly value="resume" />
              <SimpleDropdown
                selectedKeys={
                  jobMatchConfig.resume_node_id
                    ? [String(jobMatchConfig.resume_node_id)]
                    : []
                }
                placeholder={`${t('Select', 'Select')}...`}
                options={fileUploadNodeOptions}
                onChange={(option) => {
                  if ('key' in option && option.key) {
                    setJobMatchConfig({
                      ...jobMatchConfig,
                      resume_node_id: parseInt(option.key),
                    });
                    setShowJobMatchCriteriaSelector(false);
                  }
                }}
                disabled={readOnly}
                classNames={{wrapper: css.withLeftMargin}}
              />
              {!readOnly && (
                <CloseIcon
                  ariaLabel={t('Close_Icon', 'Close Icon')}
                  color="clickable"
                  onClick={() => {
                    setJobMatchConfig({
                      ...jobMatchConfig,
                      resume_node_id: null,
                    });
                    setShowResumePicker(false);
                  }}
                  size="medium"
                  type="regular"
                />
              )}
            </div>
          )}

          {!readOnly &&
            (showJobMatchCriteriaSelector ? (
              <div className={classify(css.column, css.withMargin)}>
                <Smallest>
                  {t('Job_Match_Criteria', 'Job Match Criteria')}
                </Smallest>

                <SimpleDropdown
                  // @ts-expect-error - TS2769 - No overload matches this call.
                  searchable="true"
                  placeholder={`${t('Select', 'Select')}...`}
                  selectedKeys={[]}
                  options={dynamicJobMatchCriteriaOptions.map((option) => ({
                    ...option,
                    label: t(makeKey(option.label), option.label),
                  }))}
                  onChange={(option) => {
                    if (option.key) {
                      // @ts-expect-error - TS2339 - Property 'criteriaInCategory' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
                      if (option.criteriaInCategory) {
                        const criteria = {
                          ...jobMatchConfig.criteria,
                        } as const;
                        // @ts-expect-error - TS2339 - Property 'criteriaInCategory' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'. | TS7031 - Binding element 'id' implicitly has an 'any' type.
                        option.criteriaInCategory.forEach(({id}) => {
                          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly state?: { reference_node_ids: { node_id?: number | undefined; field_name: string; }[]; ats_variables: string[]; default_values?: string[] | undefined; } | undefined; readonly skills?: { reference_node_ids: { ...; }[]; ats_variables: string[]; default_values?: string[] | undefined; } | undefined; ... 7 mor...'.
                          criteria[id] = {
                            reference_node_ids: [],
                            ats_variables: [],
                            default_values: [],
                          };
                        });
                        setJobMatchConfig({
                          ...jobMatchConfig,
                          criteria,
                        });
                      } else if (option.key === 'resume_url') {
                        setShowResumePicker(true);
                      } else {
                        setJobMatchConfig({
                          ...jobMatchConfig,
                          criteria: {
                            ...jobMatchConfig.criteria,
                            [option.key]: {
                              reference_node_ids: [],
                              ats_variables: [],
                              default_values: [],
                            },
                          },
                        });
                      }
                      setShowJobMatchCriteriaSelector(false);
                    }
                  }}
                  disabled={readOnly}
                />
              </div>
            ) : (
              <GenesisButton
                classNames={{wrapper: css.genesisButton}}
                iconLeftName="plus"
                onClick={() => setShowJobMatchCriteriaSelector(true)}
                size="medium"
                type="secondary"
              >
                {t('Add_job_match_criteria', 'Add job match criteria')}
              </GenesisButton>
            ))}

          <div className={classify(css.column, css.withMargin)}>
            <Smallest>{`${t(
              'Sort_job_match_results_by',
              'Sort job match results by',
            )}:`}</Smallest>

            <SimpleDropdown
              // @ts-expect-error - TS2769 - No overload matches this call.
              searchable="true"
              selectedKeys={
                jobMatchConfig.sort_param ? [jobMatchConfig.sort_param] : []
              }
              placeholder={`${t('Select', 'Select')}...`}
              options={filteredSortOrderOptions.map((option) => {
                return {
                  key: option.key,
                  label: t(makeKey(option.label ?? ''), option.label ?? ''),
                };
              })}
              onChange={(option) => {
                if (option.key) {
                  setJobMatchConfig({
                    ...jobMatchConfig,
                    sort_param: option.key,
                  });
                  setShowJobMatchCriteriaSelector(false);
                }
              }}
              disabled={readOnly}
            />
          </div>
        </>
        {error?.criteria?.errors?.includes('EMPTY') && (
          <p className={css.errorText}>
            {t(
              'Please_include_at_least_one_job_match_criteria',
              'Please include at least one job match criteria',
            )}
          </p>
        )}
        {error?.criteria_dependency?.errors?.map((err: string) => (
          <p className={css.errorText} key={err}>
            {t(makeKey(err), err)}
          </p>
        ))}
        {error?.sort_param_dependency?.errors?.map((err: string) => (
          <p className={css.errorText} key={err}>
            {t(makeKey(err), err)}
          </p>
        ))}
      </div>
      <div className={css.collapsibleHeader}>{t('Message', 'Message')}</div>
      <div className={classify(css.section, css.padded)}>
        {error?.message?.errors?.includes('MISSING') && (
          <p className={css.errorText}>
            {t(
              'Please_fill_out_all_required_fields',
              'Please fill out all required fields',
            )}
          </p>
        )}
        <div className={css.column}>
          <Smallest>{`${t(
            'If_matching_jobs_are_available_show_this_message',
            'If matching jobs are available, show this message',
          )}*:`}</Smallest>
          {searchResultFormattingEnabled ? (
            <RichEditor
              defaultValue={jobMatchConfig.matching_messages_rte}
              onChange={(matching_messages_rte) =>
                setJobMatchConfig({...jobMatchConfig, matching_messages_rte})
              }
              editorHeight="120px"
              includeFormatting={Boolean(richTextEditorFormattingEnabled)}
              includeHyperlink={!chatbotNewHyperlinkModalEnabled}
              includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
              includeGif
              // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
              nodeVariables={mappedNodeVariables}
              atsVariables={
                graph.use_case === 'sourcing' ? undefined : dynamicLabels
              }
              readOnly={isFieldReadOnly(
                'job_search_criteria_data.matching_messages_rte',
              )}
              entityType={graph.anchor_entity_type}
              prepareContentOnLoad={prepareContentOnLoad}
              enableDraftJSONCopy
            />
          ) : (
            <MessageDraftEditor
              placeholder={`${t(
                'We_found_job_matches_for_you_Here_they_are',
                'We found job matches for you! Here they are',
              )}:`}
              defaultValue={jobMatchConfig.matching_messages_rte}
              onChange={(matching_messages_rte) =>
                setJobMatchConfig({...jobMatchConfig, matching_messages_rte})
              }
              entityType={entityType}
              isFirstSmsMessage={isFirstSmsMessage}
              label=""
              error={error?.matching_message}
              readOnly={isFieldReadOnly(
                'job_search_criteria_data.matching_messages_rte',
              )}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
            />
          )}
        </div>

        {/* Search result editor*/}

        {searchResultFormattingEnabled && (
          <div className={css.column}>
            <Smallest>
              {t(
                'Compose_how_the_job_variables_will_appear',
                'Compose how the job variables will appear',
              )}
            </Smallest>
            <RichEditor
              defaultValue={jobMatchConfig.job_result_rte}
              onChange={(job_result_rte) =>
                setJobMatchConfig({...jobMatchConfig, job_result_rte})
              }
              editorHeight="120px"
              includeFormatting={Boolean(richTextEditorFormattingEnabled)}
              readOnly={readOnly}
              entityType={graph.anchor_entity_type}
              prepareContentOnLoad={prepareContentOnLoad}
              jobResultVariables={jobResultVariables}
              enableDraftJSONCopy
            />
          </div>
        )}

        <div className={css.multiItemRow}>
          <div className={css.column}>
            <Smallest>
              {t(
                'Maximum_number_of_matches_to_show',
                'Maximum number of matches to show',
              )}
            </Smallest>

            {/* @ts-expect-error - TS2739 - Type '{ searchable: true; value: { value: number; label: string; } | undefined; options: { value: number; label: string; }[]; onChange: (option: { value: number; label: string; } | null | undefined) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: number; label: string; }>': resolveKey, resolveLabel */}
            <Select
              searchable
              value={maxMatchesOptions.find(
                (option) => option.value === jobMatchConfig.max_matches,
              )}
              options={maxMatchesOptions}
              onChange={(option) => {
                if (option?.value) {
                  setJobMatchConfig({
                    ...jobMatchConfig,
                    max_matches: option.value,
                  });
                }
              }}
              disabled={isFieldReadOnly('job_search_criteria_data.max_matches')}
            />
          </div>
          {searchResultFormattingEnabled && (
            <div className={css.column}>
              <Smallest>
                {t('Display_matches_as', 'Display matches as')}
              </Smallest>

              {/* @ts-expect-error - TS2739 - Type '{ searchable: true; value: { value: string; label: string; } | undefined; options: { value: string; label: string; }[]; onChange: (option: { value: string; label: string; } | null | undefined) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: string; label: string; }>': resolveKey, resolveLabel */}
              <Select
                searchable
                value={resultDisplayOptions.find(
                  (option) =>
                    option.value === jobMatchConfig.result_display_style,
                )}
                options={resultDisplayOptions}
                onChange={(option) => {
                  if (option?.value) {
                    setJobMatchConfig({
                      ...jobMatchConfig,
                      result_display_style: option.value,
                    });
                  }
                }}
                disabled={readOnly}
              />
            </div>
          )}
        </div>

        {searchResultFormattingEnabled && (
          <div className={css.jobSearchToggles}>
            <Toggle
              ariaLabel={t('Toggle_Label', 'Toggle Label')}
              classNames={{
                wrapper: css.toggleContainer,
              }}
              labelPosition="right"
              checked={jobMatchConfig.show_all_jobs_button}
              onChange={(v) => {
                return setJobMatchConfig({
                  ...jobMatchConfig,
                  show_all_jobs_button: v.checked,
                });
              }}
            >
              <div className={css.column}>
                <strong>
                  {t('Show_see_all_jobs_link', 'Show ‘see all jobs’ link')}
                </strong>
                <div>
                  {t(
                    'Allows_users_to_navigate_to_full_list_of_jobs',
                    'Allows users to navigate to full list of jobs',
                  )}
                </div>
              </div>
            </Toggle>
            <Toggle
              ariaLabel={t('Toggle_Label', 'Toggle Label')}
              classNames={{
                wrapper: css.toggleContainer,
              }}
              labelPosition="right"
              checked={jobMatchConfig.link_to_job}
              onChange={(v) => {
                return setJobMatchConfig({
                  ...jobMatchConfig,
                  link_to_job: v.checked,
                });
              }}
            >
              <div className={css.column}>
                <strong>
                  {t('Enable_Job_Detail_Link', 'Enable Job Detail Link')}
                </strong>
                <div>
                  {t(
                    'Toggle_to_linkunlink_the_job_descriptions_in_match_results',
                    'Toggle to link/unlink the job descriptions in match results',
                  )}
                </div>
              </div>
            </Toggle>
          </div>
        )}

        {searchResultFormattingEnabled && (
          <div className={classify(css.column, css.jobSearchPreviewContainer)}>
            <BodySmallBold color="disabled">
              {t('Preview', 'Preview')}
            </BodySmallBold>
            <div className={css.jobSearchPreviewInner}>
              <Avatar
                color="blue"
                iconName="face-party"
                iconType="regular"
                imageSrc="https://cdn.sensehq.com/genesis/hashed/static/images/personas/1.png"
                size="medium"
                // disabled as names are not needed to be translated
                // eslint-disable-next-line @spaced-out/i18n/no-static-labels
                text="Adam Craig Gilchrist"
              />
              <div className={css.column}>
                <div className={css.jobSearchPreviewMessage}>
                  <RtePreview
                    defaultValue={jobMatchConfig.matching_messages_rte}
                    entityType={graph.anchor_entity_type}
                    prepareContentOnLoad={prepareContentOnLoad}
                    key={Math.random()}
                  />
                </div>
                <div className={css.jobSearchPreviewMessage}>
                  {jobMatchConfig.show_all_jobs_button && (
                    <div className={css.jobCategoryCardHeader}>
                      <div>{`25 ${t('matches_found', 'matches found')}`}</div>
                      <div className={css.seeAllJobs}>
                        {t('See_All', 'See All')}
                      </div>
                    </div>
                  )}
                  <List listStyle={jobMatchConfig.result_display_style}>
                    {take(
                      getPreviewJobResult(t),
                      jobMatchConfig.max_matches,
                    ).map((attrs) => {
                      return (
                        <ListItem
                          listStyle={jobMatchConfig.result_display_style}
                          key={attrs.title}
                        >
                          {
                            <RichEditorPreview
                              defaultValue={jobMatchConfig.job_result_rte}
                              variablePreviewValues={attrs}
                              key={attrs.title}
                            />
                          }
                        </ListItem>
                      );
                    })}
                  </List>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={css.column}>
          <Smallest>
            {t('If_no_matches_are_available', 'If no matches are available')}
          </Smallest>
          {/* @ts-expect-error - TS2739 - Type '{ value: { value: "show_recent" | "show_none"; label: string; } | undefined; options: { value: "show_recent" | "show_none"; label: string; }[]; onChange: (option: { value: "show_recent" | "show_none"; label: string; } | null | undefined) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: "show_recent" | "show_none"; label: string; }>': resolveKey, resolveLabel */}
          <Select
            value={noMatchHandlingOptions.find(
              (option) => option.value === jobMatchConfig.no_matches_handling,
            )}
            options={noMatchHandlingOptions}
            onChange={(option) => {
              if (option?.value) {
                setJobMatchConfig({
                  ...jobMatchConfig,
                  no_matches_handling: option.value,
                });
              }
            }}
            disabled={isFieldReadOnly(
              'job_search_criteria_data.no_matches_handling',
            )}
          />
        </div>

        <div className={css.column}>
          <Smallest>{`${t(
            'If_no_matches_available_show_this_message',
            'If no matches available, show this message',
          )}*`}</Smallest>
          {searchResultFormattingEnabled ? (
            <RichEditor
              defaultValue={jobMatchConfig.no_matches_message_rte}
              onChange={(no_matches_message_rte) =>
                setJobMatchConfig({...jobMatchConfig, no_matches_message_rte})
              }
              editorHeight="120px"
              includeFormatting={Boolean(richTextEditorFormattingEnabled)}
              includeHyperlink={!chatbotNewHyperlinkModalEnabled}
              includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
              includeGif
              // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
              nodeVariables={mappedNodeVariables}
              atsVariables={
                graph.use_case === 'sourcing' ? undefined : dynamicLabels
              }
              readOnly={isFieldReadOnly(
                'job_search_criteria_data.no_matches_message_rte',
              )}
              entityType={graph.anchor_entity_type}
              prepareContentOnLoad={prepareContentOnLoad}
              enableDraftJSONCopy
            />
          ) : (
            <MessageDraftEditor
              placeholder={`${t(
                'Sorry_we_didn\'t_find_any_matches_but_here_are_some_other_available_jobs',
                'Sorry, we didn\'t find any matches, but here are some other available jobs',
              )}:`}
              defaultValue={jobMatchConfig.no_matches_message_rte}
              onChange={(no_matches_message_rte) =>
                setJobMatchConfig({...jobMatchConfig, no_matches_message_rte})
              }
              entityType={entityType}
              label=""
              isFirstSmsMessage={isFirstSmsMessage}
              error={error?.no_matches_message}
              readOnly={isFieldReadOnly(
                'job_search_criteria_data.no_matches_message_rte',
              )}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
            />
          )}
        </div>
      </div>
      <div className={css.wideFooter}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={(hasSubmitted && !!validation) || readOnly}
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}

type TokenOptions =
  | Array<string>
  | Array<
      | string
      | {
          label: string;
          value: string;
        }
      | {
          label: string;
          value: {
            node_id: Id | undefined;
            name: string;
          };
        }
    >;

function JobMatchCriterionPicker({
  index,
  criteria_name,
  label,
  values,
  options,
  jobMatchConfig,
  setJobMatchConfig,
}: {
  index: number;
  criteria_name: Criteria;
  label: string;
  values: TokenOptions;
  options: TokenOptions;
  // these anys are the same thing, the jm config
  jobMatchConfig: JobMatchConfig;
  setJobMatchConfig: (arg1: JobMatchConfig) => unknown;
}) {
  const {t} = useLabelTranslation();
  const [interactedType, setInteractedType] = React.useState<
    'variable' | 'default' | undefined
  >(
    (jobMatchConfig.criteria[criteria_name]?.default_values?.length ?? 0) > 0
      ? 'default'
      : values.length > 0
      ? 'variable'
      : undefined,
  );

  return (
    <div>
      <label className={css.inputContainer}>
        <Input
          label={`${t('Job_match_criteria', 'Job match criteria')} ${
            index + 1
          }`}
          value={label}
          disabled
        />
        {criteria_name === 'worksite_options' && (
          <FormInputMedium
            className={css.defaultValueLabel}
            color="secondary"
          >{`${t(
            'Or_enter_default_value',
            'Or, enter default value',
          )}:`}</FormInputMedium>
        )}
      </label>
      <label className={css.inputContainer}>
        <Smallest>{` ${t(
          'Pick_Node_Value_or_VAR',
          'Pick Node Value or VAR',
        )}`}</Smallest>
        {/* @ts-expect-error - TS2741 - Property 'resolveLabel' is missing in type '{ searchable: true; isExpandable: true; disabled: boolean; isHeightLimited: boolean; classNames: { container: string; }; values: TokenOptions; allowArbitraryValues: false; options: TokenOptions; resolveKey: (token: string | ... 1 more ... | { ...; }) => string; onChange: (tokens: (string | ... 1 more ... | { ...; })...' but required in type 'SenseTypeaheadProps<string | { label: string; value: string; } | { label: string; value: { node_id: number | undefined; name: string; }; }>'. */}
        <TokenListInput
          searchable
          isExpandable
          disabled={interactedType != null && interactedType !== 'variable'}
          isHeightLimited={true}
          classNames={{container: css.tokenInput}}
          values={values}
          allowArbitraryValues={false}
          options={options}
          resolveKey={(token) => {
            if (typeof token !== 'string' && typeof token.value === 'string') {
              return token.value;
            } else {
              // @ts-expect-error - TS2551 - Property 'value' does not exist on type 'string | { label: string; value: string; } | { label: string; value: { node_id: number | undefined; name: string; }; }'. Did you mean 'valueOf'? | TS2551 - Property 'value' does not exist on type 'string | { label: string; value: string; } | { label: string; value: { node_id: number | undefined; name: string; }; }'. Did you mean 'valueOf'?
              return `${token.value.node_id}${token.value.name}`;
            }
          }}
          onChange={(tokens) => {
            tokens.length > 0
              ? setInteractedType('variable')
              : setInteractedType(undefined);
            // note here tokens will have a mixed type for nodes vs ats_variables
            // for nodes, the token will have a type of
            // {label: string, value: {node_id?: Id, name: string}}
            // whereas for ats vars the type is more like
            // {label: string, value: DynamicLabelExtra.attributeName | DynamicLabelExtra.value}
            // for sourcing flows, this is always just node values
            // otherwise it's mixed, which is why we do this conditional array pushing etc.
            // see writebackOptions / tokenOptions
            const nodes: Array<string> = [];
            const vars: Array<never> = [];

            tokens.forEach((token) => {
              if (typeof token !== 'string' && /<(.*)>/g.test(token.label)) {
                // @ts-expect-error - TS2345 - Argument of type '{ label: string; value: string; } | { label: string; value: { node_id: number | undefined; name: string; }; }' is not assignable to parameter of type 'never'.
                vars.push(token);
              } else {
                // @ts-expect-error - TS2345 - Argument of type 'string | { label: string; value: string; } | { label: string; value: { node_id: number | undefined; name: string; }; }' is not assignable to parameter of type 'string'.
                nodes.push(token);
              }
            });

            const criteria = {...jobMatchConfig.criteria} as const;
            // @ts-expect-error - TS2540 - Cannot assign to 'compensation_amount_min' because it is a read-only property. | TS2540 - Cannot assign to 'compensation_currency' because it is a read-only property. | TS2540 - Cannot assign to 'compensation_frequency' because it is a read-only property. | TS2540 - Cannot assign to 'job_title' because it is a read-only property. | TS2540 - Cannot assign to 'keywords' because it is a read-only property. | TS2540 - Cannot assign to 'location' because it is a read-only property. | TS2540 - Cannot assign to 'skills' because it is a read-only property. | TS2540 - Cannot assign to 'state' because it is a read-only property. | TS2540 - Cannot assign to 'worksite_options' because it is a read-only property.
            criteria[criteria_name] = {
              ...criteria[criteria_name],
              reference_node_ids: nodes.map((option) => ({
                // @ts-expect-error - TS2551 - Property 'value' does not exist on type 'string'. Did you mean 'valueOf'?
                node_id: option.value?.node_id,
                // @ts-expect-error - TS2551 - Property 'value' does not exist on type 'string'. Did you mean 'valueOf'?
                field_name: option.value?.name,
              })),
              // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'never'.
              ats_variables: vars.map((option) => option.value),
            };
            setJobMatchConfig({
              ...jobMatchConfig,
              criteria,
            });
          }}
        />
        {criteria_name === 'worksite_options' && (
          <SimpleCheckboxDropdown
            disabled={interactedType != null && interactedType !== 'default'}
            values={
              jobMatchConfig.criteria.worksite_options?.default_values ?? []
            }
            onChange={(nextDefaults) => {
              nextDefaults.length > 0
                ? setInteractedType('default')
                : setInteractedType(undefined);
              setJobMatchConfig(
                produce(jobMatchConfig, (draft) => {
                  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                  draft.criteria['worksite_options'].default_values =
                    nextDefaults;
                }),
              );
            }}
            options={[
              {key: 'hybrid', label: t('Hybrid', 'Hybrid')},
              {key: 'onsite', label: t('OnSite', 'On-Site')},
              {key: 'remote', label: t('Remote', 'Remote')},
            ]}
          />
        )}
      </label>
    </div>
  );
}

function JobMatchCriteriaPickerV2({
  // @ts-expect-error - TS7031 - Binding element 'values' implicitly has an 'any' type.
  values,
  // @ts-expect-error - TS7031 - Binding element 'jobMatchConfig' implicitly has an 'any' type.
  jobMatchConfig,
  // @ts-expect-error - TS7031 - Binding element 'setJobMatchConfig' implicitly has an 'any' type.
  setJobMatchConfig,
  // @ts-expect-error - TS7031 - Binding element 'criteriaData' implicitly has an 'any' type.
  criteriaData,
  // @ts-expect-error - TS7031 - Binding element 'nodeIdOptions' implicitly has an 'any' type.
  nodeIdOptions,
  // @ts-expect-error - TS7031 - Binding element 'writebackOptions' implicitly has an 'any' type.
  writebackOptions,
  allowRemove = true,
  // @ts-expect-error - TS7031 - Binding element 'includeATSVariables' implicitly has an 'any' type.
  includeATSVariables,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
}) {
  const {t, makeKey, language} = useLabelTranslation();
  const {id, name, max_number_of_values_allowed, input_type, category} =
    criteriaData;

  const dropdownOptionGroups = React.useMemo(
    () => [
      {
        groupTitle: t('Default_Options', 'Default Options'),
        groupType: 'default_values',
        options: criteriaData.options,
      },
      {
        groupTitle: t('Node_Variables', 'Node Variables'),
        groupType: 'reference_node_ids',
        options: nodeIdOptions,
      },
    ],
    [language],
  );

  if (includeATSVariables) {
    dropdownOptionGroups.push({
      groupTitle: t('ATS_Variables', 'ATS Variables'),
      groupType: 'ats_variables',
      options: writebackOptions,
    });
  }

  const handleSelectToken = (label: string, value: string, type: string) => {
    const criteria = {...jobMatchConfig.criteria} as const;
    if (type === 'ats_variables') {
      criteria[id] = {
        ...criteria[id],
        ats_variables: [...criteria[id].ats_variables, String(value)],
      };
    } else if (type === 'reference_node_ids') {
      criteria[id] = {
        ...criteria[id],
        reference_node_ids: [
          ...criteria[id].reference_node_ids,
          {
            // @ts-expect-error - TS2339 - Property 'node_id' does not exist on type 'string'.
            node_id: value?.node_id,
            // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'string'.
            field_name: value?.name,
          },
        ],
      };
    } else if (type === 'default_values') {
      criteria[id] = {
        ...criteria[id],
        default_values: [...(criteria[id].default_values ?? []), String(value)],
      };
    }

    setJobMatchConfig({
      ...jobMatchConfig,
      criteria,
    });
  };

  // @ts-expect-error TS2304
  const handleRemoveToken = (token: MixVariableToken) => {
    const criteria = {...jobMatchConfig.criteria} as const;

    if (token.type === 'reference_node_ids') {
      criteria[id] = {
        ...criteria[id],
      };
      criteria[id][token.type] = criteria[id][token.type].filter(
        // @ts-expect-error - TS7031 - Binding element 'node_id' implicitly has an 'any' type. | TS7031 - Binding element 'field_name' implicitly has an 'any' type.
        ({node_id, field_name}) => {
          const tokenNodeId = token.value.node_id;
          return !(token.value.name === field_name && tokenNodeId === node_id);
        },
      );
    } else if (token.type) {
      criteria[id] = {
        ...criteria[id],
      };
      criteria[id][token.type] =
        criteria[id][token.type]?.filter(
          (variable: any) => variable !== token.value,
        ) ?? [];
    }

    setJobMatchConfig({
      ...jobMatchConfig,
      criteria,
    });
  };

  return (
    <div className={css.jobMatchCriteriaInputRow}>
      <GenesisInput readOnly value={t(makeKey(name), name)} />
      <MixVariableInput
        dropdownOptionGroups={dropdownOptionGroups}
        values={values}
        handleSelectToken={handleSelectToken}
        handleRemoveToken={handleRemoveToken}
        allowArbitraryValues={
          input_type.includes('DEFAULT_VALUE') && !criteriaData.options?.length
        }
        disabled={readOnly}
      />
      {allowRemove && !readOnly && (
        <CloseIcon
          ariaLabel={t('Close_Icon', 'Close Icon')}
          color="clickable"
          onClick={() => {
            setJobMatchConfig({
              ...jobMatchConfig,
              criteria: omit({...jobMatchConfig.criteria}, [id]),
            });
          }}
          size="medium"
          type="regular"
        />
      )}
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'defaultValue' implicitly has an 'any' type. | TS7031 - Binding element 'entityType' implicitly has an 'any' type. | TS7031 - Binding element 'prepareContentOnLoad' implicitly has an 'any' type.
const RtePreview = ({defaultValue, entityType, prepareContentOnLoad}) => (
  <RichEditor
    defaultValue={defaultValue}
    onChange={() => {}}
    readOnly
    entityType={entityType}
    prepareContentOnLoad={prepareContentOnLoad}
    expandToFit
    editorHeight="12px"
  />
);

/**
 * SimpleCheckboxDropdown
 * @param {MenuOption[]} options an array of menu options
 * @param {string[]} values an arary of strings represented selected 'keys'
 * @param {onChangeHandler} a callback to execute on change, receives an array of selected keys
 */
function SimpleCheckboxDropdown({
  disabled,
  options,
  values,
  onChange,
}: {
  disabled: boolean;
  options: MenuOption[];
  values: string[];
  onChange: (options: string[]) => unknown;
}) {
  const {t} = useLabelTranslation();
  const dropdownInputText = getTextLabelFromSelectedKeys(values, options);
  return (
    <Dropdown
      dropdownInputText={dropdownInputText}
      placeholder={`${t('Select', 'Select')}...`}
      disabled={disabled}
      menu={{
        options,
        optionsVariant: 'checkbox',
        selectedKeys: values,
      }}
      onChange={(option) => {
        onChange(getSelectedKeysFromSelectedOption(option, values));
      }}
    />
  );
}

// @ts-expect-error - TS7031 - Binding element 'listStyle' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type.
function List({listStyle, children}) {
  switch (listStyle) {
    case 'Bulleted List':
      return <ul className={css.column}>{children}</ul>;
    case 'Numbered List':
      return <ol className={css.column}>{children}</ol>;
    default:
      return <>{children}</>;
  }
}

// @ts-expect-error - TS7031 - Binding element 'listStyle' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type.
function ListItem({listStyle, children}) {
  if (['Bulleted List', 'Numbered List'].includes(listStyle)) {
    return <li className={css.warningParagraph}>{children}</li>;
  } else {
    return <div className={css.warningParagraph}>{children}</div>;
  }
}

const defaultJobSearchParams = [
  'location',
  'state',
  'job_title',
  'skills',
  'keywords',
  'distance',
  'distance_unit',
  'zipcode',
];

const jobSearchParams = {
  worksite_options: 'discoverAllowWorksiteOption',
  disciplines: 'discoverAllowDiscipline',
  specialties: 'discoverAllowSpecialty',
  certifications: 'discoverAllowCertification',
  employment_types: 'discoverAllowEmploymentType',
  compensation_amount_min: 'discoverAllowPayrate',
  compensation_currency: 'discoverAllowPayrate',
  compensation_frequency: 'discoverAllowPayrate',
  shift_preference: 'discoverAllowShift',
  contract_duration_min: 'discoverAllowContractDuration',
} as const;

const defaultJobResultFormat = {
  blocks: [
    {
      key: '8ok28',
      text: '{Title}',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [
        {
          offset: 0,
          length: 7,
          style: 'BOLD',
        },
      ],
      entityRanges: [
        {
          offset: 0,
          length: 7,
          key: 0,
        },
      ],
      data: {},
    },
    {
      key: '7hnok',
      text: '{Location}',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [
        {
          offset: 0,
          length: 10,
          key: 1,
        },
      ],
      data: {},
    },
  ],
  entityMap: {
    '0': {
      type: 'JOB_RESULT_ATTRIBUTE',
      mutability: 'IMMUTABLE',
      data: {
        name: 'title',
        job_attribute: 'title',
      },
    },
    '1': {
      type: 'JOB_RESULT_ATTRIBUTE',
      mutability: 'IMMUTABLE',
      data: {
        name: 'location',
        job_attribute: 'location',
      },
    },
  },
} as const;

const defaultMatchingMessage = {
  blocks: [
    {
      data: {},
      depth: 0,
      entityRanges: [],
      inlineStyleRanges: [],
      key: '77j8h',
      text: 'We found job matches for you! Here they are:',
      type: 'unstyled',
    },
  ],
  entityMap: {},
} as const;

const getPreviewJobResult = (
  t: (val: string, defaultVal: string) => string,
) => [
  {
    title: 'Senior Software Engineer',
    certifications: 'None required',
    compensation: 'Competitive salary and benefits package',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Information Technology',
    employment_types: 'Full-time',
    company_name: 'Tech Innovations Inc.',
    location: 'San Francisco, CA',
    shifts: 'Day shifts',
    specialties: t('Software_Development', 'Software Development'),
    url: 'ExampleURL.com/senior-software-engineer',
    worksite_options: 'On-site',
    summary:
      'Join a dynamic team of software engineers working on cutting-edge projects.',
    description:
      'We are seeking a talented Senior Software Engineer to join our team in San Francisco. You will be responsible for designing, developing, and maintaining software applications.',
    state: 'California',
  },
  {
    title: 'Marketing Specialist',
    certifications: 'Marketing certifications preferred',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Marketing',
    employment_types: 'Full-time',
    company_name: 'Bright Ideas Marketing Agency',
    location: 'Los Angeles, CA',
    shifts: 'Day shifts',
    specialties: t('Digital_Marketing', 'Digital Marketing'),
    url: 'ExampleURL.com/marketing-specialist',
    worksite_options: 'On-site',
    summary: 'Exciting opportunity to join a fast-paced marketing agency.',
    description:
      'We are looking for a Marketing Specialist to develop and execute marketing strategies for our clients. This role involves working closely with clients to understand their business objectives and implementing marketing campaigns to achieve results.',
    state: 'California',
  },
  {
    title: 'Financial Analyst',
    certifications: 'CPA or CFA preferred',
    compensation: 'Competitive salary and bonus structure',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Finance',
    employment_types: 'Full-time',
    company_name: 'Capital Investments LLC',
    location: 'San Jose, CA',
    shifts: 'Day shifts',
    specialties: 'Investment Analysis',
    url: 'ExampleURL.com/financial-analyst',
    worksite_options: 'On-site',
    summary: 'Join a leading investment firm as a Financial Analyst.',
    description:
      'We are seeking a Financial Analyst to provide financial analysis and investment recommendations to our clients. This role involves analyzing market trends, financial statements, and economic data to make informed investment decisions.',
    state: 'California',
  },
  {
    title: 'Registered Nurse',
    certifications: 'RN license required',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Healthcare',
    employment_types: 'Full-time',
    company_name: 'Mercy Hospital',
    location: 'Sacramento, CA',
    shifts: 'Rotating shifts',
    specialties: 'Critical Care Nursing',
    url: 'ExampleURL.com/registered-nurse',
    worksite_options: 'On-site',
    summary:
      'Join our team of dedicated healthcare professionals at Mercy Hospital.',
    description:
      'We are looking for experienced Registered Nurses to provide compassionate care to patients in our critical care unit. This role involves assessing patient needs, administering medication, and collaborating with other healthcare professionals to ensure quality patient care.',
    state: 'California',
  },
  {
    title: 'Graphic Designer',
    certifications: 'None required',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Design',
    employment_types: 'Full-time',
    company_name: 'Creative Concepts Agency',
    location: 'San Diego, CA',
    shifts: 'Day shifts',
    specialties: 'Branding and Identity',
    url: 'ExampleURL.com/graphic-designer',
    worksite_options: 'On-site',
    summary:
      'Exciting opportunity for a talented Graphic Designer to join our team.',
    description:
      'We are seeking a creative and experienced Graphic Designer to develop visual concepts for print and digital media. This role involves collaborating with clients to understand their design needs and producing high-quality graphic designs.',
    state: 'California',
  },
  {
    title: 'Human Resources Manager',
    certifications: 'SHRM certification preferred',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Human Resources',
    employment_types: 'Full-time',
    company_name: 'PeopleFirst Inc.',
    location: 'San Francisco, CA',
    shifts: 'Day shifts',
    specialties: 'Talent Acquisition',
    url: 'ExampleURL.com/hr-manager',
    worksite_options: 'On-site',
    summary: 'Join a dynamic HR team as a Human Resources Manager.',
    description:
      'We are looking for an experienced Human Resources Manager to oversee all aspects of HR operations. This role involves recruiting and onboarding new employees, managing employee relations, and implementing HR policies and procedures.',
    state: 'California',
  },
  {
    title: 'Sales Representative',
    certifications: 'None required',
    compensation: 'Competitive base salary plus commission',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Sales',
    employment_types: 'Full-time',
    company_name: 'SalesPro Solutions',
    location: 'Los Angeles, CA',
    shifts: 'Day shifts',
    specialties: 'B2B Sales',
    url: 'ExampleURL.com/sales-representative',
    worksite_options: 'On-site',
    summary:
      'Exciting opportunity for a motivated Sales Representative to join our team.',
    description:
      'We are seeking a Sales Representative to generate new business opportunities and maintain relationships with existing clients. This role involves identifying prospects, making sales presentations, and closing deals to achieve sales targets.',

    state: 'California',
  },
  {
    title: 'Project Manager',
    certifications: 'PMP certification preferred',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Project Management',
    employment_types: 'Full-time',
    company_name: 'ProjectWorks Inc.',
    location: 'San Jose, CA',
    shifts: 'Day shifts',
    specialties: 'IT Project Management',
    url: 'ExampleURL.com/project-manager',
    worksite_options: 'On-site',
    summary:
      'Join our team as a Project Manager and lead exciting IT projects.',
    description:
      'We are looking for an experienced Project Manager to oversee the planning, execution, and delivery of IT projects. This role involves coordinating with stakeholders, managing project timelines and budgets, and ensuring project success.',
    state: 'California',
  },
  {
    title: 'Customer Service Representative',
    certifications: 'None required',
    compensation: 'Competitive salary and benefits',
    contract_duration: 'Full-time, permanent',
    disciplines: 'Customer Service',
    employment_types: 'Full-time',
    company_name: 'Service Solutions LLC',
    location: 'San Diego, CA',
    shifts: 'Rotating shifts',
    specialties: 'Technical Support',
    url: 'ExampleURL.com/customer-service-representative',
    worksite_options: 'On-site',
    summary:
      'Join our team as a Customer Service Representative and provide exceptional support to our customers.',
    description:
      'We are seeking friendly and motivated Customer Service Representatives to assist customers with product inquiries, technical support, and issue resolution. This role involves communicating with customers via phone, email, and chat to ensure a positive customer experience.',
    state: 'California',
  },
  {
    title: 'Environmental Scientist',
    certifications: 'None required',
    compensation: 'Competitive salary and benefits',

    contract_duration: 'Full-time, permanent',
    disciplines: 'Environmental Science',
    employment_types: 'Full-time',
    company_name: 'EcoTech Solutions',
    location: 'Sacramento, CA',
    shifts: 'Day shifts',
    specialties: 'Pollution Control',
    url: 'ExampleURL.com/environmental-scientist',
    worksite_options: 'On-site',
    summary:
      'Exciting opportunity for an Environmental Scientist to make a difference.',
    description:
      'We are looking for an Environmental Scientist to conduct fieldwork, collect samples, and analyze data to assess environmental impact. This role involves working on projects related to pollution control, environmental remediation, and sustainability initiatives.',
    state: 'California',
  },
];
